import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { Grid, Box, TableCell, TableRow, TableBody, Typography, Paper, Select, MenuItem, createTheme } from "@mui/material";

import useTable from "../../../components/customHelpers/useTable";
import Controls from "../../../components/controls/Controls";
import { PurchaseOrder } from "../../../actions/purchaseOrder";
import PurchaseOrderForm from "../../../components/forms/purchaseOrderForm";
import ConfirmDialog from "../../../components/customHelpers/ConfirmDialog";
import { useHistory } from "react-router-dom";
import { FiEdit, FiTrash2, FiEye, FiUpload } from "react-icons/fi";
import Loading from "../../../components/customHelpers/Loading";
import Notify from "../../../components/customHelpers/Notify";
import Notification from "../../../components/customHelpers/Notification";
import Popup from "../../../components/customHelpers/Popup";
import Layout from "../../../components/layout/Layout";
import Admin from "../../../components/auth/Admin";
import produce from "immer";
import PublishPurchaseProductForm from "../../../components/forms/publishPurchaseProductForm";
import { formatNumber } from "../../../utils/utils";
import Norecords from "../../../components/customHelpers/Norecords";
import InfoComponent from "../../../components/InfoComponent";
import { IoMdAdd } from "react-icons/io";
import { Search } from "@mui/icons-material";
const theme = createTheme();

const useStyles = makeStyles(() => ({
  pageContent: {
    margin: theme.spacing(5),
    padding: theme.spacing(3),
  },
  searchInput: {
    width: "75%",
  },
  newButton: {
    position: "absolute",
    right: "10px",
  },
}));

const headCells = [
  { id: "id", label: "Id" },
  { id: "name", label: "Name" },
  { id: "status", label: "Status" },
  { id: "totalPurchasePrice", label: "Total price" },
  { id: "createdAt", label: "Order date" },
  { id: "actions", label: "Actions", disableSorting: true },
];

export default function PurchaseOrdersPage() {
  const classes = useStyles();
  const history = useHistory();
  const [records, setRecords] = useState([]);
  const { viewAllPurchaseOrders, deletePurchaseOrder } = PurchaseOrder();

  const [filterFn, setFilterFn] = useState({
    fn: (items) => {
      return items;
    },
  });
  const [openPopup, setOpenPopup] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const { NotifyMessage, notify, setNotify } = Notify();
  const [openPublishPopup, setOpenPublishPopup] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [values, setValues] = useState({
    loading: false,
    reload: false,
  });
  const { loading, reload } = values;

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable(records, headCells, filterFn);

  useEffect(() => {
    loadPurchaseOrders();
  }, []);

  const loadPurchaseOrders = () => {
    setValues({ ...values, loading: true });
    viewAllPurchaseOrders()
      .then((response) => {
        if (response.err) {
          NotifyMessage({
            message: response.err,
            type: "error",
          });
        } else {
          setRecords(response.purchaseOrder);
          setValues({ ...values, loading: false });
        }
      })
      .catch((err) => {
        NotifyMessage({
          message: "Failed to fetch purchase orders",
          type: "error",
        });
        setValues({ ...values, loading: false });
      })
      .finally(() => {
        setValues({ ...values, loading: false });
      });
  };

  const handleSearch = (e) => {
    e.target.value = e.target.value.trimLeft();
    let target = e.target;
    setFilterFn({
      fn: (items) => {
        const columns = ["name", "status", "totalPurchasePrice"];

        if (target.value == "") return items;
        else {
          return items.filter((x) => {
            return columns.some((column) => {
              return (
                x[column]
                  .toString()
                  .toLowerCase()
                  .indexOf(target.value.toLowerCase()) > -1
              );
            });
          });
        }
      },
    });
  };
  const openInPopup = (item) => {
    setRecordForEdit(item);
    setOpenPopup(true);
  };

  const onDelete = (id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });

    deletePurchaseOrder(id)
      .then((response) => {
        if (response.err) {
          NotifyMessage({
            message: response.err,
            type: "error",
          });
        } else {
          NotifyMessage({
            message: "Purchase order deleted successfully",
            type: "success",
          });
          setRecords(
            produce(records, (draft) => {
              const index = records.findIndex((item) => item.id === id);
              if (index !== -1) draft.splice(index, 1);
            })
          );
        }
      })
      .catch((err) => {
        NotifyMessage({
          message: "Failed to delete purchase order",
          type: "error",
        });
      });
  };

  return (
    <Layout>
      <Admin>
        <div className="card">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: { xs: 1, sm: 0 },
              "& > .newButton": {
                marginLeft: { xs: "auto", sm: "0" },
              },
              "@media (max-width: 455px)": {
                flexDirection: "column",
                alignItems: "flex-start",
              },
            }}
          >
            <Typography
              variant="h6"
              className="page-header"
              sx={{ whiteSpace: "nowrap" }}
            >
              Purchase Orders
            </Typography>
            <Controls.Button
              text="Add Purchase Order"
              variant="outlined"
              startIcon={<IoMdAdd />}
              className="newButton"
              onClick={() => {
                setOpenPopup(true);
                setRecordForEdit(null);
              }}
            />
          </Box>
          <InfoComponent pageKey="purchaseOrders" />

          <div
            className="search-header-container"
            style={{ marginBottom: 0, flex: 1, maxWidth: 260, minWidth: 260 }}
          >
            <div className="search-header-flex">
              <div className="search-container">
                <Search className="search_icon" />
                <input
                  type="text"
                  onChange={handleSearch}
                  placeholder="Search Purchase Orders"
                  className="search-input"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <Paper style={{ overflowX: "auto" }}>
              <TblContainer>
                <TblHead />
                <TableBody>
                  {recordsAfterPagingAndSorting().length > 0 ? (
                    recordsAfterPagingAndSorting().map((item) => (
                      <TableRow key={item._id}>
                        <TableCell style={{ width: "10%" }}>
                          {item.orderId ? item.orderId : item.id}
                        </TableCell>
                        <TableCell style={{ width: "10%" }}>
                          {item.name}
                        </TableCell>
                        <TableCell style={{ width: "15%" }}>
                          {item.status}
                        </TableCell>
                        <TableCell
                          style={{ width: "15%" }}
                          className="page-table-cell"
                        >
                          <div className="page-total">
                            {formatNumber(item.totalPurchasePrice)}
                          </div>
                        </TableCell>
                        <TableCell style={{ width: "15%" }}>
                          {new Date(item.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell
                          style={{ width: "20%", whiteSpace: "nowrap" }}
                        >
                          <Controls.ActionButton
                            color="primary"
                            title="Edit"
                            onClick={() => {
                              openInPopup(item);
                            }}
                          >
                            <FiEdit />
                          </Controls.ActionButton>
                          {!item?.purchaseLists > 0 && (
                            <Controls.ActionButton
                              title="Delete"
                              color="secondary"
                              onClick={() => {
                                setConfirmDialog({
                                  isOpen: true,
                                  title: "Are you sure to delete this record?",
                                  subTitle: "You can't undo this operation",
                                  onConfirm: () => {
                                    onDelete(item.id);
                                  },
                                });
                              }}
                            >
                              <FiTrash2 />
                            </Controls.ActionButton>
                          )}

                          <Controls.ActionButton
                            color="primary"
                            onClick={() => {
                              history.push(`/purchase-product/${item.id}`);
                            }}
                            title="View Detail"
                          >
                            <FiEye />
                          </Controls.ActionButton>
                          {item?.purchaseLists?.length > 0 &&
                            item.status === "IN-PURCHASE" && (
                              <Controls.ActionButton
                                color="primary"
                                title="Publish"
                                onClick={() => {
                                  setOpenPublishPopup(true);
                                  setRecordForEdit(item);
                                }}
                              >
                                <FiUpload fontSize="medium" />
                              </Controls.ActionButton>
                            )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <Norecords col={6} />
                  )}
                </TableBody>
              </TblContainer>
              <TblPagination />
            </Paper>
          )}

          <Notification notify={notify} setNotify={setNotify} />
          <Popup
            title="Purchase Order Form"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
          >
            <PurchaseOrderForm
              recordForEdit={recordForEdit}
              setOpenPopup={setOpenPopup}
              setPurchaseOrders={loadPurchaseOrders}
              NotifyMessage={NotifyMessage}
            />
          </Popup>
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <Popup
            title="Publish Purchase Product"
            openPopup={openPublishPopup}
            setOpenPopup={setOpenPublishPopup}
          >
            <PublishPurchaseProductForm
              NotifyMessage={NotifyMessage}
              setOpenPopup={setOpenPublishPopup}
              recordForEdit={recordForEdit}
              getAllProducts={loadPurchaseOrders}
            />
          </Popup>
        </div>
      </Admin>
    </Layout>
  );
}
